"use client"

import { useState, useEffect, useRef } from "react"
import { Zap, Star, Trophy } from "lucide-react"

interface XpProgressProps {
  current: number
  max: number
}

export default function XpProgress({ current, max }: XpProgressProps) {
  const [displayPercentage, setDisplayPercentage] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const percentage = (current / max) * 100
  const progressBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsAnimating(true)
    let start = 0
    const duration = 1500
    const increment = 20
    const stepTime = Math.abs(duration / (percentage / increment))
    
    const timer = setInterval(() => {
      start += increment
      setDisplayPercentage(start)
      if (start >= percentage) {
        setDisplayPercentage(percentage)
        clearInterval(timer)
        setTimeout(() => setIsAnimating(false), 300)
      }
    }, stepTime)

    return () => clearInterval(timer)
  }, [percentage])

  const getLevelTitle = () => {
    if (percentage < 25) return "Beginner"
    if (percentage < 50) return "Learner"
    if (percentage < 75) return "Achiever"
    if (percentage < 90) return "Expert"
    return "Master"
  }

  return (
    <section className="mb-8 relative">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900">Next Level Progress</h3>
          <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-bold rounded-full">
            {getLevelTitle()}
          </span>
        </div>
        <p className="text-sm font-medium text-blue-600 flex items-center gap-1">
          <Zap className="w-4 h-4" />
          {current.toLocaleString()} / {max.toLocaleString()} XP
        </p>
      </div>

      {/* Progress Bar Container */}
      <div className="relative pt-8 pb-4">
        {/* Floating Avatar */}
        <div 
          className="absolute z-10 transition-all duration-700 ease-out"
          style={{ 
            left: `${Math.min(displayPercentage, 98)}%`,
            transform: 'translateX(-50%)',
            top: '0px'
          }}
        >
          <div className={`relative ${isAnimating ? 'animate-bounce' : ''}`}>
            {/* Avatar Container */}
            <div className="relative group">
              {/* Avatar/Icon - EASILY REPLACEABLE */}
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-yellow-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                {/* 🔄 EASY TO REPLACE: Change this div to an img tag or different icon */}
                <svg 
                  className="w-6 h-6 text-white" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  aria-label="Superman avatar"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                {/* Alternative: Use an img tag for gallery image */}
                {/* <img 
                  src="/path-to-your-gallery-image.jpg" 
                  alt="User avatar"
                  className="w-full h-full rounded-full object-cover"
                /> */}
              </div>
              
              {/* Level Badge */}
              <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                {Math.floor(percentage / 20) + 1}
              </div>
              
              {/* XP Tooltip */}
              <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {current.toLocaleString()} XP
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45"></div>
              </div>
            </div>
            
            {/* Animation Trail */}
            {isAnimating && (
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                <Star className="w-3 h-3 text-yellow-400 animate-ping" />
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar Track */}
        <div className="relative">
          <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden shadow-inner">
            {/* Animated Progress Fill */}
            <div
              ref={progressBarRef}
              className="bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-600 h-4 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
              style={{ width: `${displayPercentage}%` }}
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>
          
          {/* Progress Markers */}
          <div className="absolute top-0 left-0 w-full h-4 flex justify-between px-1">
            {[0, 25, 50, 75, 100].map((marker) => (
              <div key={marker} className="relative">
                <div className="w-[2px] h-4 bg-white/50" />
                <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-slate-500 font-medium">
                  {marker}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Level Indicators */}
        <div className="flex justify-between mt-6">
          <div className="text-center">
            {/* <div className="text-xs text-slate-500">Current Level</div> */}
            <div className="font-bold text-lg text-teal-600">{Math.floor(percentage / 20)}</div>
          </div>
          {/* <div className="text-center">
            <div className="text-xs text-slate-500">Progress</div>
            <div className="font-bold text-lg text-blue-600">{displayPercentage.toFixed(1)}%</div>
          </div> */}
          <div className="text-center">
            {/* <div className="text-xs text-slate-500">Next Level</div> */}
            <div className="font-bold text-lg text-indigo-600">{Math.floor(percentage / 20) + 1}</div>
          </div>
        </div>
      </div>

      {/* Add CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </section>
  )
}